import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import axios from "axios";
import { getThemeTextColor } from "../utils/themeUtils";
import { useTheme } from "@mui/material/styles";

const CountrySelectionPopup = ({ open, onClose, onConfirm, title, selectedCountries = [], defaultSelectAll = false }) => {
  const theme = useTheme();
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCountryIds, setSelectedCountryIds] = useState(new Set());

  useEffect(() => {
    if (open) {
      fetchCountries();
      setSearchTerm("");
    }
  }, [open]);

  useEffect(() => {
    // Filter countries based on search term
    if (searchTerm.trim() === "") {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter((country) => country.countryName.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredCountries(filtered);
    }
  }, [countries, searchTerm]);

  // Initialize selection when popup opens
  useEffect(() => {
    if (open && countries.length > 0) {
      if (selectedCountries && selectedCountries.length > 0) {
        // Convert selectedCountries to strings and match with country.countryId (numbers)
        const selectedStrings = selectedCountries.map((id) => id.toString());
        const matchingCountryIds = countries
          .filter((country) => selectedStrings.includes(country.countryId.toString()))
          .map((country) => country.countryId);
        setSelectedCountryIds(new Set(matchingCountryIds));
      } else if (defaultSelectAll) {
        setSelectedCountryIds(new Set(countries.map((country) => country.countryId)));
      } else {
        setSelectedCountryIds(new Set());
      }
    }
  }, [open, selectedCountries, defaultSelectAll, countries]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/v1/countries");
      setCountries(response.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryToggle = (countryId) => {
    const newSelectedIds = new Set(selectedCountryIds);
    if (newSelectedIds.has(countryId)) {
      newSelectedIds.delete(countryId);
    } else {
      newSelectedIds.add(countryId);
    }
    setSelectedCountryIds(newSelectedIds);
    // Don't call onConfirm here - only update internal state
  };

  const handleSelectAll = () => {
    // Select all filtered countries
    const newSelectedIds = new Set(selectedCountryIds);
    filteredCountries.forEach((country) => {
      newSelectedIds.add(country.countryId);
    });
    setSelectedCountryIds(newSelectedIds);
    // Don't call onConfirm here - only update internal state
  };

  const handleDeselectAll = () => {
    // Deselect all countries
    setSelectedCountryIds(new Set());
    // Don't call onConfirm here - only update internal state
  };

  const handleConfirm = () => {
    // Send the current selection to parent and close the popup
    // Convert numbers to strings for parent component
    onConfirm(Array.from(selectedCountryIds).map((id) => id.toString()));
    onClose();
  };

  const handleCancel = () => {
    setSearchTerm("");
    onClose();
  };

  const selectedCount = selectedCountryIds.size;
  const filteredCount = filteredCountries.length;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search countries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" size="small" onClick={handleSelectAll} disabled={filteredCount === 0}>
              Select All
            </Button>
            <Button variant="outlined" size="small" onClick={handleDeselectAll} disabled={filteredCount === 0}>
              Deselect All
            </Button>
            <Typography variant="body2" color="text.secondary">
              ({selectedCount} selected)
            </Typography>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {loading ? (
            <Typography sx={{ ...getThemeTextColor(theme) }}>Loading countries...</Typography>
          ) : (
            <Stack spacing={1}>
              {filteredCountries.map((country) => (
                <FormControlLabel
                  key={country.countryId}
                  control={
                    <Checkbox
                      checked={selectedCountryIds.has(country.countryId)}
                      onChange={() => handleCountryToggle(country.countryId)}
                    />
                  }
                  label={country.countryName}
                />
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountrySelectionPopup;
